import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { linkedVersesService } from '../../../services/supabaseService';
import { bibleService } from '../../../services/bibleService';
import { useAuth } from '../../../contexts/AuthContext';

const RELATIONSHIP_OPTIONS = [
  'Cross Reference',
  'Related Theme',
  'Parallel Passage',
  'Fulfillment',
  'Contrast',
  'Related',
];

const AddLinkModal = ({ isOpen, onClose, sourceVerse, book, chapter, version }) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [relationship, setRelationship] = useState('Cross Reference');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const searchTimeout = useRef(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedVerse(null);
      setRelationship('Cross Reference');
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setSelectedVerse(null);
    setError('');
    if (searchTimeout?.current) clearTimeout(searchTimeout?.current);
    if (!query?.trim() || query?.trim()?.length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await bibleService?.searchVerses(query, 15);
        setSearchResults(data || []);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const handleSave = async () => {
    if (!selectedVerse) {
      setError('Please select a verse to link.');
      return;
    }
    if (!user?.id || !sourceVerse?.id) {
      setError('You must be signed in and have this verse in your library to add links.');
      return;
    }
    setSaving(true);
    setError('');
    const ref = selectedVerse?.reference;
    const text = selectedVerse?.text;
    const ver = selectedVerse?.version_name || version?.toUpperCase() || 'KJV';

    const { error: saveError } = await linkedVersesService?.addLinkedVerseByReference(
      user?.id,
      sourceVerse?.id,
      ref,
      text,
      ver,
      relationship
    );

    setSaving(false);
    if (saveError) {
      setError(saveError?.message || 'Failed to save link.');
      return;
    }
    setSuccess(`Linked ${ref} successfully!`);
    setTimeout(() => {
      onClose?.();
    }, 1200);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60] animate-in fade-in duration-200">
      <div className="bg-card rounded-lg max-w-lg w-full shadow-2xl border border-border animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 bg-primary/10 rounded-lg">
              <Icon name="Link" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Add Cross Reference</h2>
              {sourceVerse && (
                <p className="text-xs text-muted-foreground">
                  Linking from: <span className="font-medium text-foreground">{sourceVerse?.reference}</span>
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Source verse info */}
          {!sourceVerse?.id && (
            <div className="p-3 bg-warning/10 border border-warning/30 rounded-lg text-xs text-warning">
              <Icon name="AlertTriangle" size={14} className="inline mr-1" />
              This verse must be in your library to save links. Add it to your library first.
            </div>
          )}

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Search any Bible verse
            </label>
            <div className="relative">
              <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e?.target?.value)}
                placeholder='e.g. "John 3:16", "faith", "Romans 8"'
                className="w-full pl-9 pr-3 py-2 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Search by reference or keyword across the entire Bible</p>
          </div>

          {/* Results */}
          {searchLoading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary" />
              Searching...
            </div>
          )}

          {!searchLoading && searchResults?.length > 0 && (
            <div className="border border-border rounded-md overflow-hidden max-h-52 overflow-y-auto">
              {searchResults?.map((result, idx) => (
                <button
                  key={result?.verse_id || idx}
                  onClick={() => {
                    setSelectedVerse(result);
                    setSearchQuery(result?.reference);
                    setSearchResults([]);
                  }}
                  className={`w-full text-left px-3 py-2.5 text-sm border-b border-border last:border-b-0 transition-colors ${
                    selectedVerse?.verse_id === result?.verse_id
                      ? 'bg-primary/10 text-primary' :'text-foreground hover:bg-muted/50'
                  }`}
                >
                  <div className="font-medium text-xs text-primary mb-0.5">{result?.reference}</div>
                  <div className="text-muted-foreground text-xs leading-relaxed line-clamp-2">
                    {result?.text}
                  </div>
                </button>
              ))}
            </div>
          )}

          {!searchLoading && searchQuery?.trim()?.length >= 2 && searchResults?.length === 0 && (
            <p className="text-xs text-muted-foreground py-1">No results found. Try a different reference or keyword.</p>
          )}

          {/* Selected verse preview */}
          {selectedVerse && (
            <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="text-xs font-semibold text-primary mb-1">{selectedVerse?.reference}</div>
              <p className="text-xs text-foreground leading-relaxed">
                {selectedVerse?.text?.slice(0, 200)}{selectedVerse?.text?.length > 200 ? '…' : ''}
              </p>
            </div>
          )}

          {/* Relationship */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Relationship Type</label>
            <select
              value={relationship}
              onChange={(e) => setRelationship(e?.target?.value)}
              className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {RELATIONSHIP_OPTIONS?.map(opt => (
                <option key={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* Error / Success */}
          {error && (
            <p className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
              {error}
            </p>
          )}
          {success && (
            <p className="text-xs text-success bg-success/10 border border-success/20 rounded-md px-3 py-2 flex items-center gap-1.5">
              <Icon name="CheckCircle2" size={13} />
              {success}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={saving || !selectedVerse || !sourceVerse?.id}
          >
            {saving ? (
              <span className="flex items-center gap-1.5">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <Icon name="Link" size={13} />
                Save Link
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddLinkModal;
